import { isTestLevelId, opFor, parseLevelId, type LevelId } from './ids';
import { LEVELS, SEGMENTS } from './levels';
import { WORLD_TUNING, scrollSpeedFor } from './tuning';
import type { CheckpointDef, ColumnDef, EnemyDef, LevelDef, PickupDef, SegmentDef, SegmentKind, SegmentPhase } from './types';

/** Everything of a level placed in absolute x, ready for the runner (#34). Deeply frozen. */
export interface ResolvedLevel {
  readonly def: LevelDef;
  readonly length: number;
  readonly segmentStarts: readonly {
    readonly id: string;
    readonly start: number;
    readonly length: number;
    readonly kind: SegmentKind;
  }[];
  readonly columns: readonly (ColumnDef & { readonly segmentId: string })[];
  readonly enemies: readonly (EnemyDef & { readonly segmentId: string })[];
  readonly pickups: readonly (PickupDef & { readonly segmentId: string })[];
  readonly checkpoints: readonly CheckpointDef[];
  /** Phase of each pattern segment, in order (GDD §10.2). */
  readonly phases: readonly (SegmentPhase | undefined)[];
}

export interface LevelRegistry {
  levels: Readonly<Record<string, LevelDef>>;
  segments: Readonly<Record<string, SegmentDef>>;
}

function deepFreeze<T>(value: T): T {
  if (value && typeof value === 'object' && !Object.isFrozen(value)) {
    Object.freeze(value);
    for (const child of Object.values(value)) deepFreeze(child);
  }
  return value;
}

function checkHeader(def: LevelDef): void {
  const problem = isTestLevelId(def.id) ? testHeaderProblem(def) : campaignHeaderProblem(def);
  if (problem) throw new Error(`Invalid level ${def.id}: ${problem}`);
}

function campaignHeaderProblem(def: LevelDef): string | null {
  const id = def.id as LevelId;
  const { world } = parseLevelId(id);
  if (def.op !== opFor(id)) return `op "${def.op}" does not match level id "${def.id}"`;
  if (def.world !== world) return `world ${def.world} does not match level id "${def.id}"`;
  if (def.scrollSpeed !== scrollSpeedFor(id)) {
    return `scrollSpeed ${def.scrollSpeed} does not match tuning ${scrollSpeedFor(id)} for "${def.id}"`;
  }
  return null;
}

/** The test level (OP-000) takes the tuning of its declared world, level 1. */
function testHeaderProblem(def: LevelDef): string | null {
  if (def.op !== 'OP-000') return `op "${def.op}" must be "OP-000"`;
  if (!def.test) return 'a test level must be marked test: true';
  if (def.scrollSpeed !== WORLD_TUNING[def.world].scrollSpeedLevel1) {
    return `scrollSpeed ${def.scrollSpeed} does not match the tuning of world ${def.world}`;
  }
  return null;
}

/**
 * Loads a level and resolves its segments in order into absolute x (GDD §15.6). Segment x values are
 * local; checkpoints are already absolute. The header must agree with its id and the tuning table.
 */
export function loadLevel(id: string, registry: LevelRegistry = { levels: LEVELS, segments: SEGMENTS }): ResolvedLevel {
  const def = registry.levels[id];
  if (!def) throw new Error(`Unknown level "${id}"`);
  checkHeader(def);
  if (def.segments.length === 0) throw new Error(`level ${def.id} has no segments`);

  const segmentStarts: ResolvedLevel['segmentStarts'][number][] = [];
  const columns: ResolvedLevel['columns'][number][] = [];
  const enemies: ResolvedLevel['enemies'][number][] = [];
  const pickups: ResolvedLevel['pickups'][number][] = [];
  const phases: (SegmentPhase | undefined)[] = [];
  let start = 0;
  for (const ref of def.segments) {
    const segment = registry.segments[ref];
    if (!segment) throw new Error(`Unknown segment "${ref}" in level ${def.id}`);
    segmentStarts.push({ id: segment.id, start, length: segment.length, kind: segment.kind ?? 'pattern' });
    if ((segment.kind ?? 'pattern') === 'pattern') phases.push(segment.phase);
    for (const column of segment.columns) columns.push({ ...column, x: start + column.x, segmentId: segment.id });
    for (const enemy of segment.enemies) {
      // The far end of a cable car (`x2`) is local to its segment like `x`; the resolved level holds both in absolute x.
      const params =
        enemy.type === 'EN-12' && enemy.params?.x2 !== undefined ? { ...enemy.params, x2: start + enemy.params.x2 } : enemy.params;
      enemies.push({ ...enemy, ...(params ? { params } : {}), x: start + enemy.x, segmentId: segment.id });
    }
    for (const pickup of segment.pickups) pickups.push({ ...pickup, x: start + pickup.x, segmentId: segment.id });
    start += segment.length;
  }

  return deepFreeze({
    def: structuredClone(def),
    length: start,
    segmentStarts,
    columns,
    enemies,
    pickups,
    checkpoints: def.checkpoints.map((checkpoint) => ({ ...checkpoint })),
    phases,
  });
}
